import { Injectable } from '@angular/core';
import { StatisticToModuleVisitModel } from './statistic-to-module-visit.model';
import { FiBackendService } from '../fi-backend/fi-backend.service';
import { StatisticToObjectVisitModel } from './statistic-to-object-visit.model';
import { AccessRightService } from '../fi-shared-services/access-right.service';
import { AppTextService } from '../app-text';
import { ModuleTypesMap } from '../models/module-types';
import { FiStateService } from '../fi-state-proxy/fi-state.proxy.service';
import { FiStateChangeService } from '../fi-state-proxy/fi-state-change.service';
import { FiUserDataService } from '../fi-shared-services/fi-user-data.service';
import { FiCustomerDataService } from '../fi-shared-services/fi-customer-data.service';
import { MobileAppHelperBaseService } from '../fi-shared-services/mobile-app-helper-base.service';
import { Observable, Subject, Subscription } from 'rxjs';
import { removeItem } from '@fi-shared/utils/remove-item';
import { ModuleType, ObjectTypes } from '@fi-shared/types';

const StatisticsSaveToBackendInterval = 20000;
const StatisticsUpdateEndTimeInterval = 5000;

declare var ga;

interface IStatisticModel {
    modulesVisits: StatisticToModuleVisitModel[];
    currentModuleVisit: StatisticToModuleVisitModel;
    objectVisits: StatisticToObjectVisitModel[];
}

@Injectable()
export class StatisticService {
    private storageKey = 'fi-statistics';
    private saveInProgress = false;
    private sub: Subscription;

    private _currentModuleChanged$ = new Subject<ModuleType>();
    get currentModuleChanged$(): Observable<ModuleType> {
        return this._currentModuleChanged$.asObservable();
    }

    constructor(
        private fiBackend: FiBackendService,
        private accessRightService: AccessRightService,
        private appTextService: AppTextService,
        private mobileAppService: MobileAppHelperBaseService,
        private stateService: FiStateService,
        private fiStateChangeService: FiStateChangeService,
        private customerDataService: FiCustomerDataService,
        private userDataService: FiUserDataService,
    ) {}

    private getModelFromLocalStorage() {
        const model: IStatisticModel = {
            modulesVisits: [],
            currentModuleVisit: null,
            objectVisits: [],
        };

        const data = localStorage.getItem(this.storageKey);
        if (data) {
            try {
                const obj: IStatisticModel = JSON.parse(data);
                (obj.modulesVisits || []).forEach((visitData) => {
                    const moduleVisit = new StatisticToModuleVisitModel(this.userDataService.getTokenId());
                    moduleVisit.Initialize(visitData);
                    model.modulesVisits.push(moduleVisit);
                });

                if (obj.currentModuleVisit) {
                    const moduleVisit = new StatisticToModuleVisitModel(this.userDataService.getTokenId());
                    moduleVisit.Initialize(obj.currentModuleVisit);
                    model.currentModuleVisit = moduleVisit;
                }

                (obj.objectVisits || []).forEach((visitData) => {
                    const objectVisit = new StatisticToObjectVisitModel(this.userDataService.getTokenId());
                    objectVisit.Initialize(visitData);
                    model.objectVisits.push(objectVisit);
                });
            } catch (e) {
                console.warn('FI: Failed to load statistic data. Statistic cleared.');
                localStorage.removeItem(this.storageKey);
            }
        }

        return model;
    }

    private saveToLocalStorage(model) {
        localStorage.setItem(this.storageKey, JSON.stringify(model));
    }

    private saveToBackend(doSwapCurrentModuleVisit?: boolean) {
        if (this.saveInProgress || !this.userDataService.authorized) {
            return Promise.resolve();
        }

        if (doSwapCurrentModuleVisit) {
            this.swapCurrentModuleVisit();
        }

        let model = this.getModelFromLocalStorage();
        const moduleVisitsToSave = model.modulesVisits.slice(0);
        const objectVisitsToSave = model.objectVisits.slice(
            0,
            this.isLastObjectVisitInCurrentModule(model) ? model.objectVisits.length - 1 : undefined,
        );
        const currentObjectVisitToSave = this.isLastObjectVisitInCurrentModule(model)
            ? model.objectVisits[model.objectVisits.length - 1]
            : undefined;

        if (!moduleVisitsToSave.length && !objectVisitsToSave.length && !currentObjectVisitToSave) {
            return Promise.resolve();
        }
        this.saveInProgress = true;
        const promise = this.fiBackend.fetchDataPost<void>(
            'Statistic/Statistic/SaveStatisticsVisits',
            {
                ModuleVisits: moduleVisitsToSave,
                ObjectVisits: currentObjectVisitToSave
                    ? objectVisitsToSave.concat([currentObjectVisitToSave])
                    : objectVisitsToSave,
            },
            {
                supressErrorMessage: true,
            },
        );
        promise
            .then(
                () => {
                    model = this.getModelFromLocalStorage();
                    moduleVisitsToSave.forEach((savedModuleVisit) => {
                        const existingModuleVisit = model.modulesVisits.find(
                            (item) =>
                                item.SessionId == savedModuleVisit.SessionId &&
                                item.StartTime == savedModuleVisit.StartTime &&
                                item.ModuleType == savedModuleVisit.ModuleType,
                        );
                        if (existingModuleVisit) {
                            removeItem(model.modulesVisits, existingModuleVisit);
                        } else {
                            console.warn('Saved module visit not found', savedModuleVisit, model.modulesVisits);
                        }
                    });

                    objectVisitsToSave.forEach((savedObjectVisit) => {
                        const existingObjectVisit = model.objectVisits.find(
                            (item) =>
                                item.SessionId == savedObjectVisit.SessionId &&
                                item.StartTime == savedObjectVisit.StartTime &&
                                item.ObjectId == savedObjectVisit.ObjectId &&
                                item.ObjectType == savedObjectVisit.ObjectType,
                        );

                        if (existingObjectVisit) {
                            removeItem(model.objectVisits, existingObjectVisit);
                        } else {
                            console.warn('Saved object visit not found', existingObjectVisit, model.objectVisits);
                        }
                    });
                    this.saveToLocalStorage(model);
                },
                () => {
                    // todo do something on time out , maybe pause statistics saving... https://chainformation.atlassian.net/browse/FI-3359
                },
            )
            .finally(() => {
                this.saveInProgress = false;
            });
        return promise;
    }

    private swapCurrentModuleVisit(skipUpdatingEndTime?: boolean) {
        if (!skipUpdatingEndTime) {
            this.updateEndTime();
        }
        const model = this.getModelFromLocalStorage();
        if (model.currentModuleVisit) {
            model.modulesVisits.push(model.currentModuleVisit);
            model.currentModuleVisit = null;
            this.setLastObjectVisitInCurrentModule(model, false);
        } else {
            this.onCurrentModuleChanged(undefined);
        }
        this.saveToLocalStorage(model);
    }

    private updateEndTime() {
        const model = this.getModelFromLocalStorage();
        if (model.currentModuleVisit) {
            model.currentModuleVisit.EndTime = new Date();

            if (this.isLastObjectVisitInCurrentModule(model)) {
                model.objectVisits[model.objectVisits.length - 1].EndTime = model.currentModuleVisit.EndTime;
            }
        }
        this.saveToLocalStorage(model);
    }

    private isLastObjectVisitInCurrentModule(model: IStatisticModel) {
        return (
            model.currentModuleVisit &&
            model.objectVisits.length > 0 &&
            model.objectVisits[model.objectVisits.length - 1].IsInCurrentModule
        );
    }
    private setLastObjectVisitInCurrentModule(model: IStatisticModel, isInCurrentModule: boolean) {
        if (model.currentModuleVisit && model.objectVisits.length) {
            model.objectVisits[model.objectVisits.length - 1].IsInCurrentModule = isInCurrentModule;
        }
    }

    private addObjectVisit(model: IStatisticModel, moduleType: ModuleType, objectType: ObjectTypes, objectId: number) {
        if (model.objectVisits.length > 0) {
            const currentObject = model.objectVisits.slice(-1)[0];
            if (currentObject.ObjectType === objectType && currentObject.ObjectId === objectId) {
                currentObject.EndTime = new Date();
                return;
            }
        }
        const objectVisit = new StatisticToObjectVisitModel(
            this.userDataService.getTokenId(),
            moduleType,
            objectType,
            objectId,
        );
        model.objectVisits.push(objectVisit);
    }

    Visit(moduleType: ModuleType, objectType?: ObjectTypes, objectId?: number) {
        // sometimes Visit is fired before InitStatistics invoking, so we should execute swapCurrentModuleVisit in that case
        this.checkSwapCurrentModuleVisitOnStart();

        this.updateEndTime();
        const model = this.getModelFromLocalStorage();
        const currentModuleType = model.currentModuleVisit?.ModuleType;
        if (!model.currentModuleVisit) {
            model.currentModuleVisit = new StatisticToModuleVisitModel(this.userDataService.getTokenId(), moduleType);
        }

        if (objectType) {
            this.addObjectVisit(model, moduleType, objectType, objectId);
            this.saveToLocalStorage(model);
            this.saveToBackend();
        } else {
            this.setLastObjectVisitInCurrentModule(model, false);
            this.saveToLocalStorage(model);
        }
        if (currentModuleType !== moduleType) {
            this.onCurrentModuleChanged(moduleType);
        }
    }

    private saveToBackendInterval: NodeJS.Timeout;
    private updateEndTimeInterval: NodeJS.Timeout;

    InitStatistics() {
        this.checkSwapCurrentModuleVisitOnStart();
        this.sub = this.fiStateChangeService.onSuccess$.subscribe((transition) => {
            const newState = transition.to();
            const model = this.getModelFromLocalStorage();
            if (!model.currentModuleVisit) {
                this.swapCurrentModuleVisit();
                return;
            }
            const mtm = ModuleTypesMap;
            if (!mtm.hasOwnProperty(model.currentModuleVisit.ModuleType)) {
                this.swapCurrentModuleVisit();
                return;
            }
            const module = mtm[model.currentModuleVisit.ModuleType];
            if (!(newState.name === module.BaseState || newState.name.indexOf(module.BaseState + '.') === 0)) {
                this.swapCurrentModuleVisit();
                return;
            }
        });

        // save initial data if loaded from storage
        if (5000 < StatisticsSaveToBackendInterval) {
            setTimeout(() => this.saveToBackend(), 5000);
        }

        this.saveToBackendInterval = setInterval(() => this.saveToBackend(), StatisticsSaveToBackendInterval);
        this.updateEndTimeInterval = setInterval(() => this.updateEndTime(), StatisticsUpdateEndTimeInterval);

        window.addEventListener('message', this.onMessage.bind(this), false);
    }

    private isCurrentModuleVisitOnStartChecked = false;
    private checkSwapCurrentModuleVisitOnStart() {
        if (!this.isCurrentModuleVisitOnStartChecked) {
            this.swapCurrentModuleVisit(true);
            this.isCurrentModuleVisitOnStartChecked = true;
        }
    }

    private gaAccountInitialized = false;
    trackPageView() {
        if (!this.gaAccountInitialized && this.customerDataService.customerData.GAAccount) {
            ga('create', this.customerDataService.customerData.GAAccount, { cookieDomain: 'none' });
            this.gaAccountInitialized = true;
        }

        if (this.gaAccountInitialized) {
            /*
            if ($analytics.settings.ga.userId) {
                ga('set', '&uid', $analytics.settings.ga.userId);
            }*/

            setTimeout(() => {
                const url = window.location.pathname;
                ga('send', 'pageview', url);
            });
        }
    }

    flushStatisticToBackend() {
        return this.saveToBackend(true);
    }

    stopStatisticsGathering() {
        if (this.sub) {
            this.sub.unsubscribe();
            this.sub = undefined;
        }

        clearInterval(this.saveToBackendInterval);
        clearInterval(this.updateEndTimeInterval);
        window.removeEventListener('message', this.onMessage, false);
        this.updateEndTime();
    }

    onMessage(e) {
        if (e.data.name === 'fic.CloseAppIFrame') {
            (<any>this.flushStatisticToBackend()).finally(() => {
                if (this.mobileAppService.mobileAppMode) {
                    this.mobileAppService.canCloseAppIFrame(e.data.key);
                }
            });
            this.stopStatisticsGathering();
        }
    }

    saveFilesViewAction(mediaBankFileIds: number[]) {
        return this.fiBackend.fetchDataPost<void>(`Statistic/Statistic/SaveFilesViewAction`, mediaBankFileIds);
    }
    saveFileDownloadAction(mediaBankFileId: number) {
        return this.fiBackend.fetchDataPost<void>(
            `Statistic/Statistic/SaveFileDownloadAction/${mediaBankFileId}`,
            undefined,
            {
                supressErrorMessage: true,
            },
        );
    }

    private onCurrentModuleChanged(moduleType: ModuleType) {
        this._currentModuleChanged$.next(moduleType);
    }
}
